import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiRefreshCw,
  FiUsers,
  FiXCircle
} from 'react-icons/fi';
import { API_ENDPOINTS } from '../../utils/api';
import {
  buildAttendanceReportRows,
  buildAttendanceStats,
  exportAttendanceCsv,
  getDepartmentOptions
} from '../../utils/attendanceReportUtils';

const StatCard = ({ label, value, icon: Icon, tone = 'navy' }) => {
  const tones = {
    navy: 'text-[#1a365d] bg-[#e8f0f4]',
    emerald: 'text-emerald-700 bg-emerald-50',
    rose: 'text-rose-700 bg-rose-50',
    amber: 'text-amber-700 bg-amber-50',
    teal: 'text-teal-700 bg-teal-50'
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon />
        </div>
      </div>
    </div>
  );
};

const statusBadge = (status) => {
  const styles = {
    present: 'bg-emerald-50 text-emerald-700',
    late: 'bg-amber-50 text-amber-700',
    incomplete: 'bg-slate-100 text-slate-600',
    absent: 'bg-rose-50 text-rose-700'
  };
  return styles[status] || 'bg-slate-100 text-slate-600';
};

const AttendanceReport = () => {
  const today = new Date().toISOString().split('T')[0];
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: today, end: today });
  const [department, setDepartment] = useState('all');

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    const headers = authHeaders();
    const results = await Promise.allSettled([
      axios.get(API_ENDPOINTS.getAttendanceAll, { headers }),
      axios.get(API_ENDPOINTS.getUsers, { headers }),
      axios.get(API_ENDPOINTS.getAllLeaves, { headers })
    ]);

    setRecords(results[0].status === 'fulfilled' ? results[0].value.data || [] : []);
    setUsers(results[1].status === 'fulfilled' ? results[1].value.data || [] : []);
    setLeaves(results[2].status === 'fulfilled' ? results[2].value.data || [] : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const departments = useMemo(() => getDepartmentOptions(users), [users]);

  const reports = useMemo(
    () =>
      buildAttendanceReportRows({
        records,
        users,
        dateRange,
        department
      }),
    [records, users, dateRange, department]
  );

  const stats = useMemo(
    () =>
      buildAttendanceStats({
        rows: reports,
        users,
        leaves,
        dateRange,
        department
      }),
    [reports, users, leaves, dateRange, department]
  );

  return (
    <div className="p-4 md:p-6 bg-gradient-to-br from-slate-100 via-teal-50/30 to-slate-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-3">
              <FiCalendar className="text-teal-600" />
              Attendance Report
            </h1>
            <p className="text-slate-600 mt-1">Live employee attendance tracking and analytics</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => exportAttendanceCsv(reports)}
              disabled={!reports.length}
              className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50 text-sm"
            >
              <FiDownload /> Export CSV
            </button>
            <button
              type="button"
              onClick={loadData}
              className="px-4 py-2 rounded-lg bg-[#1a365d] text-white hover:bg-[#153050] flex items-center gap-2 text-sm"
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total Employees" value={stats.totalEmployees} icon={FiUsers} tone="navy" />
          <StatCard label="Present" value={stats.present} icon={FiCheckCircle} tone="emerald" />
          <StatCard label="Absent" value={stats.absent} icon={FiXCircle} tone="rose" />
          <StatCard label="Late" value={stats.late} icon={FiClock} tone="amber" />
          <StatCard label="On Leave" value={stats.onLeave} icon={FiCalendar} tone="teal" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5">
          <div className="flex flex-wrap gap-4 mb-5">
            <label className="text-sm">
              <span className="block text-slate-500 mb-1">Start Date</span>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                className="px-3 py-2 border border-slate-300 rounded-lg"
              />
            </label>
            <label className="text-sm">
              <span className="block text-slate-500 mb-1">End Date</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                className="px-3 py-2 border border-slate-300 rounded-lg"
              />
            </label>
            <label className="text-sm min-w-[180px]">
              <span className="block text-slate-500 mb-1">Department</span>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="all">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" />
              <p className="text-slate-500 mt-3 text-sm">Loading attendance data…</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Employee</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Employee ID</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Department</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Check In</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Check Out</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Hours</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-12 text-slate-500">
                        No attendance records found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    reports.map((report) => (
                      <tr key={report.id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{report.employee}</td>
                        <td className="px-4 py-3 text-slate-600">{report.employeeId}</td>
                        <td className="px-4 py-3 text-slate-600">{report.department}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {new Date(`${report.date}T12:00:00`).toLocaleDateString('en-IN')}
                        </td>
                        <td className="px-4 py-3">{report.checkIn}</td>
                        <td className="px-4 py-3">{report.checkOut}</td>
                        <td className="px-4 py-3">{report.hours === '—' ? '—' : `${report.hours} hrs`}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${statusBadge(
                              report.status
                            )}`}
                          >
                            {report.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport;
